import useSearchClassification from "$src/core/hooks/use-search-classification";
import useSearchClassificationConfiguration from "$src/core/hooks/use-search-classification-configuration";
import useStorageState from "$src/core/hooks/use-storage-state";
import useTranslation from "$src/core/hooks/use-translation";
import type { ContentDto } from "$src/core/models/dto";
import useSearchResultsQuery, {
  useSearchResultsQueries,
} from "$src/core/queries/use-search-results-query";
import combineStatuses from "$src/core/utils/query/combine-statuses";
import topFacetTags from "$src/core/utils/search/top-facet-tags";

type TagLane = { tag: string; items: ContentDto[] };

const MAX_LANES = 4;

/**
 * Push items already shown in an earlier lane to the back, so consecutive
 * facet rows don't start with the same items.
 */
const diversifyLaneItems = (lanes: TagLane[]): TagLane[] => {
  const seen = new Set<string>();
  return lanes.map((lane) => {
    const unique: ContentDto[] = [];
    const duplicates: ContentDto[] = [];
    for (const item of lane.items) {
      (seen.has(item.id) ? duplicates : unique).push(item);
    }
    for (const item of lane.items) seen.add(item.id);
    return { tag: lane.tag, items: [...unique, ...duplicates] };
  });
};

const useSearch = ({ query: rawQuery }: { query?: string }) => {
  const { locale } = useTranslation();

  const [conversationalStatus, setConversationalStatus] = useStorageState(
    "search.conversational.status",
    null,
  );

  const conversational = conversationalStatus === "enabled";

  const { enable: enableConversationalSearch } =
    useSearchClassificationConfiguration();
  const {
    classification,
    status: classificationStatus,
    retry: retryClassification,
  } = useSearchClassification(rawQuery, {
    locale,
    enabled: conversational,
  });

  const fullQuery = rawQuery || undefined;

  const classified =
    conversational && classificationStatus === "success"
      ? classification
      : null;
  const classifiedTags = (classified?.categories ?? []).slice(0, MAX_LANES);
  const hasClassifiedFacets = classifiedTags.length > 0;

  const fullQueryResultsQuery = useSearchResultsQuery(
    { query: fullQuery },
    { enabled: !!fullQuery && !hasClassifiedFacets },
  );

  // Once classification yields facets, the flat query is disabled but React
  // Query keeps its last data — force empty so lanes don't render over a grid.
  const results = hasClassifiedFacets
    ? []
    : (fullQueryResultsQuery.data?.items ?? []);

  const laneTags = hasClassifiedFacets ? classifiedTags : topFacetTags(results);

  const tagResultsQueries = useSearchResultsQueries(
    laneTags.map((tag) => ({ tags: [tag] })),
  );

  const relatedResults = diversifyLaneItems(
    laneTags
      .map((tag, index) => ({
        tag,
        items: tagResultsQueries[index]?.data?.items ?? [],
      }))
      .filter((lane) => lane.items.length > 0),
  );

  const combinedStatuses = [
    ...(conversational && classificationStatus !== "idle"
      ? [classificationStatus]
      : []),
    ...(fullQuery && !hasClassifiedFacets
      ? [fullQueryResultsQuery.status]
      : []),
    ...(hasClassifiedFacets
      ? tagResultsQueries.map((result) => result.status)
      : []),
  ];

  // Re-run whatever actually contributed to the current view: classification
  // plus the results queries that are active. Refetching only the enabled ones
  // avoids firing requests for a query that is currently switched off.
  const retry = async () => {
    if (conversational) retryClassification();

    const refetching: Promise<unknown>[] = [];
    if (fullQuery && !hasClassifiedFacets) {
      refetching.push(fullQueryResultsQuery.refetch());
    }
    if (hasClassifiedFacets) {
      refetching.push(...tagResultsQueries.map((result) => result.refetch()));
    }

    await Promise.all(refetching);
  };

  return {
    status:
      combinedStatuses.length > 0
        ? combineStatuses(...combinedStatuses)
        : "success",
    results,
    relatedResults,
    enableConversationalSearch,
    disableConversational: () => setConversationalStatus("disabled"),
    retry,
  };
};

export default useSearch;
